import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ContentSection,
  FooterMessage,
  ErrorMessage,
  SuccessMessage,
  asyncPaginateSelectStyles,
  StatusText,
} from "../shared/Components";
import {
  Campaign,
  CampaignManifest,
  CampaignPrefilteringJob,
  ManifestType,
} from "../../appstore/models";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Checkbox,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  TextFilter,
  Table,
  Link,
} from "@awsui/components-react";
import styled from "styled-components";
import {
  generateCampaignManifest,
  listCampaignManifests,
  listCampaignPrefilteringJobs,
} from "../../common/api";
import { DynamoDB } from "aws-sdk";
import { AsyncPaginate } from "react-select-async-paginate";
import { MapComponent } from "./CampaignMap";
import appConfig from "../../common/appConfig";
import { splitS3Uri } from "../../common/helpers";

const TableHeader = styled.div`
  justify-content: space-between;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 22px;
  &:first-child {
    font-weight: 700;
    font-size: 18px;
  }
`;

export interface NewCampaignManifestProps {
  onCancel: () => void;
  onSuccess: () => void;
  campaign: Campaign;
  visible: boolean;
}

export const NewCampaignManifestModal = (props: NewCampaignManifestProps) => {
  const { onCancel, onSuccess, campaign, visible } = props;
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [apiSuccess, setApiSuccess] = useState(false);

  const [includeAllObjects, setIncludeAllObjects] = useState(true);
  const [limit, setLimit] = useState<string>(`${campaign.TotalChipCount}`);
  const limitError = useMemo(() => {
    const val = Number(limit);
    if (!limit || isNaN(val) || parseInt(limit) !== val || val <= 0) {
      return "Limit must be a positive integer value";
    }
  }, [limit]);
  const [
    prefilteringConfidenceStartString,
    setPrefilteringConfidenceStartString,
  ] = useState<string>("0");
  const minConfidenceThresholdError = useMemo(() => {
    const val = Number(prefilteringConfidenceStartString);

    if (!prefilteringConfidenceStartString || !(val >= 0 && val <= 4)) {
      return "Must be between 0 and 1";
    }
  }, [prefilteringConfidenceStartString]);
  const [manifestType, setManifestType] = useState<ManifestType>("IMAGE");
  const [numFramesPerTask, setNumFramesPerTask] = useState<string>("1");
  const numFramesPerTaskError = useMemo<string | undefined>(() => {
    if (
      !numFramesPerTask ||
      isNaN(Number(numFramesPerTask)) ||
      Number(numFramesPerTask) !== parseInt(numFramesPerTask) ||
      Number(numFramesPerTask) <= 0
    ) {
      return "Must be an integer value > 0";
    }
  }, [numFramesPerTask]);
  const [
    selectedCampaignPrefilteringJobOption,
    setSelectedCampaignPrefilteringJobOption,
  ] = useState<
    | {
        value: string;
        label: string;
      }
    | undefined
  >();
  const [query, setQuery] = useState<string>("");

  const [campaignPrefilteringJobsByName, setCampaignPrefilteringJobsByName] =
    useState<{
      [key: string]: CampaignPrefilteringJob;
    }>({});

  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    DynamoDB.Key | undefined
  >(undefined);

  const [selectedCampaignPrefilteringJob, setSelectedCampaignPrefilteringJob] =
    useState<CampaignPrefilteringJob | undefined>(undefined);

  // Sets selected prefiltering job object based on dropdown selection
  useEffect(() => {
    if (!selectedCampaignPrefilteringJobOption) {
      setSelectedCampaignPrefilteringJob(undefined);
      return;
    }
    if (
      selectedCampaignPrefilteringJob &&
      selectedCampaignPrefilteringJob.CampaignPrefilteringId ===
        selectedCampaignPrefilteringJobOption.value
    ) {
      return;
    }
    setSelectedCampaignPrefilteringJob(
      campaignPrefilteringJobsByName[
        selectedCampaignPrefilteringJobOption.value
      ]
    );
  }, [
    campaignPrefilteringJobsByName,
    selectedCampaignPrefilteringJob,
    selectedCampaignPrefilteringJobOption,
  ]);

  const loadCampaignPrefilteringJobOptions = useCallback(
    async (newQuery: string) => {
      const response = await listCampaignPrefilteringJobs(
        campaign.JobName,
        10,
        {
          NameContains: newQuery ? newQuery : undefined,
          LastEvaluatedKey: query === newQuery ? lastEvaluatedKey : undefined,
        }
      );
      setQuery(newQuery);
      setLastEvaluatedKey(response.LastEvaluatedKey);
      const newGroundTruthLabelingJobSummariesByName: {
        [key: string]: CampaignPrefilteringJob;
      } = {};
      response.campaignPrefilteringJobs.forEach((job) => {
        newGroundTruthLabelingJobSummariesByName[job.CampaignPrefilteringId] =
          job;
      });
      setCampaignPrefilteringJobsByName({
        ...campaignPrefilteringJobsByName,
        ...newGroundTruthLabelingJobSummariesByName,
      });
      return {
        options: response.campaignPrefilteringJobs.map((job) => ({
          label: job.CampaignPrefilteringId,
          value: job.CampaignPrefilteringId,
        })),
        hasMore: response.LastEvaluatedKey !== undefined,
        additional: true,
      };
    },
    [lastEvaluatedKey, campaignPrefilteringJobsByName, query, campaign.JobName]
  );

  const create = useCallback(async () => {
    setCreating(true);
    setApiSuccess(false);
    setErrors([]);
    const linkCampaignLabelingJobResult = await generateCampaignManifest({
      campaignId: campaign.JobName,
      campaignPrefilteringId: selectedCampaignPrefilteringJob
        ? selectedCampaignPrefilteringJob.CampaignPrefilteringId
        : undefined,
      minConfidenceThreshold: Number(prefilteringConfidenceStartString),
      manifestType: manifestType,
      numFramesPerTask: Number(numFramesPerTask),
    });
    if (!linkCampaignLabelingJobResult.success) {
      setErrors(
        linkCampaignLabelingJobResult.errors || [
          "Error linking campaign to labeling job",
        ]
      );
      setCreating(false);
      setApiSuccess(false);
      return;
    }
    setCreating(false);
    setApiSuccess(true);
    onSuccess();
  }, [
    selectedCampaignPrefilteringJob,
    campaign.JobName,
    onSuccess,
    manifestType,
    prefilteringConfidenceStartString,
    numFramesPerTask,
  ]);

  useEffect(() => {
    // Hack to fix an issue where maplibre-gl map was always rendering with height 300 in the modal popup
    // Issue described here: https://github.com/mapbox/mapbox-gl-js/issues/3265
    if (visible) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [visible]);

  const submitEnabled =
    (selectedCampaignPrefilteringJob
      ? selectedCampaignPrefilteringJob && !minConfidenceThresholdError
      : true) && !numFramesPerTaskError;

  return (
    <Modal
      className="wide-modal"
      visible={visible}
      onDismiss={() => onCancel()}
      header="New Ground Truth Input Manifest"
      closeAriaLabel="Close dialog"
      footer={
        <div className="awsui-util-f-r">
          {errors ? (
            <FooterMessage>
              {errors.map((message, i) => (
                <ErrorMessage key={i} message={message} />
              ))}
            </FooterMessage>
          ) : null}
          {apiSuccess ? (
            <FooterMessage>
              <SuccessMessage>Success</SuccessMessage>
            </FooterMessage>
          ) : null}
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!submitEnabled || creating}
            onClick={() => create()}
          >
            {creating
              ? "Creating Ground Truth Manifest..."
              : "Create Ground Truth Manifest"}
          </Button>
        </div>
      }
    >
      <ColumnLayout borders="horizontal">
        <SpaceBetween size="l">
          <FormField label="Ground Truth Manifest Type">
            <Select
              selectedOption={{
                label:
                  manifestType.charAt(0) + manifestType.slice(1).toLowerCase(),
                value: manifestType,
              }}
              onChange={({ detail }) =>
                setManifestType(
                  detail.selectedOption.value
                    ? (detail.selectedOption.value as ManifestType)
                    : "IMAGE"
                )
              }
              options={[
                {
                  label: "Image",
                  value: "IMAGE",
                },
                {
                  label: "Video",
                  value: "VIDEO",
                },
              ]}
              selectedAriaLabel="Selected"
              placeholder="None"
            />
          </FormField>
          {manifestType === "VIDEO" ? (
            <FormField label="Number of frames per task">
              <Input
                value={`${numFramesPerTask}`}
                invalid={!!numFramesPerTaskError}
                onChange={({ detail: { value } }) => setNumFramesPerTask(value)}
              />
              {!!numFramesPerTaskError && (
                <ErrorMessage message={numFramesPerTaskError} />
              )}
            </FormField>
          ) : null}
          <FormField
            label="Image Chip Limit"
            description="Number of image chips to include in Ground Truth input labeling manifest"
          >
            <Checkbox
              onChange={({ detail }) => setIncludeAllObjects(detail.checked)}
              checked={includeAllObjects}
            >
              All image chips
            </Checkbox>
            <Input
              invalid={!!limitError}
              value={includeAllObjects ? `${campaign.TotalChipCount}` : limit}
              disabled={includeAllObjects}
              onChange={({ detail: { value } }) => setLimit(value)}
            />
          </FormField>

          <FormField label="[Optional] Campaign Prefiltering Job">
            <AsyncPaginate
              isClearable={true}
              styles={asyncPaginateSelectStyles}
              onChange={function (option: { value: string; label: string }) {
                setSelectedCampaignPrefilteringJobOption(option);
              }}
              value={selectedCampaignPrefilteringJobOption}
              loadOptions={loadCampaignPrefilteringJobOptions}
            ></AsyncPaginate>
          </FormField>
          <FormField label="Prefiltering Minimum Confidence Threshold">
            <Input
              invalid={!!minConfidenceThresholdError}
              value={`${prefilteringConfidenceStartString}` || ""}
              onChange={({ detail: { value } }) =>
                setPrefilteringConfidenceStartString(value)
              }
              disabled={!selectedCampaignPrefilteringJobOption}
            />
            {!!minConfidenceThresholdError && (
              <ErrorMessage message={minConfidenceThresholdError} />
            )}
          </FormField>
          <MapComponent
            campaign={campaign}
            hideSidebar={true}
            height={350}
            selectedCampaignPrefilteringJob={selectedCampaignPrefilteringJob}
            prefilteringConfidenceStart={
              selectedCampaignPrefilteringJob
                ? Number(prefilteringConfidenceStartString) || 0
                : 0
            }
          />
        </SpaceBetween>
      </ColumnLayout>
    </Modal>
  );
};

const CampaignManifestTable = (props: { campaign: Campaign }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const { campaign } = props;
  const pageSize = 8;
  const [campaignManifests, setCampaignManifests] = useState<
    CampaignManifest[]
  >([]);
  const [loadingError, setLoadingError] = useState<undefined | string>(
    undefined
  );
  const [showNewManifest, setShowNewManifest] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    DynamoDB.Key | undefined
  >(undefined);

  const fetchPage = (pageNumber: number) => {
    if (pageNumber === currentPageIndex) {
      return;
    }

    setCurrentPageIndex(pageNumber);
    if (pageNumber === pageCount && hasNextPage) {
      setSearchTriggered(true);
    }
  };
  const refresh = () => {
    setCurrentPageIndex(1);
    setCampaignManifests([]);
    setHasNextPage(true);
    setPageCount(1);
    setLastEvaluatedKey(undefined);
    setSearchTriggered(true);
  };

  const loadCampaignManifests = useCallback(async () => {
    if (!hasNextPage) {
      return;
    }
    setLoadingError(undefined);
    try {
      const listCampaignManifestsResult = await listCampaignManifests(
        campaign.JobName,
        pageSize,
        lastEvaluatedKey
      );
      setLastEvaluatedKey(listCampaignManifestsResult.LastEvaluatedKey);
      setPageCount(
        listCampaignManifestsResult.LastEvaluatedKey ? pageCount + 1 : pageCount
      );
      setCampaignManifests([
        ...campaignManifests,
        ...listCampaignManifestsResult.campaignManifests,
      ]);
      if (listCampaignManifestsResult.LastEvaluatedKey) {
        setHasNextPage(true);
      } else {
        setHasNextPage(false);
      }
    } catch (e) {
      console.log("Error loading prefiltering results", e);
      setLoadingError("Error loading prefiltering results");
    }
  }, [campaign, campaignManifests, pageCount, hasNextPage, lastEvaluatedKey]);

  useEffect(() => {
    if (searchTriggered) {
      loadCampaignManifests();
      setSearchTriggered(false);
    }
  }, [searchTriggered, loadCampaignManifests]);

  useEffect(() => {
    setSearchTriggered(true);
  }, []);

  if (loadingError) {
    return (
      <ContentSection>
        <Container
          header={<Header variant="h2">Ground Truth Input Manifests</Header>}
        >
          <div>{loadingError}</div>
        </Container>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <Table
        trackBy="CampaignManifestId"
        columnDefinitions={[
          {
            id: "Status",
            header: "Status",
            cell: (e) => {
              const statusCode =
                e.Status === "Created"
                  ? "success"
                  : e.Status === "Error"
                  ? "error"
                  : "running";
              return (
                <StatusText key={e.CampaignPrefilteringId} status={statusCode}>
                  {e.Status}
                </StatusText>
              );
            },
            sortingField: "Status",
          },
          {
            id: "CampaignManifestId",
            header: "Campaign Manifest Id",
            cell: (e) => {
              if (e.GenerateCampaignManifestStepFunctionExecutionArn) {
                const generateCampaignManifestStepFunctionExecutionConsoleUrl =
                  new URL(
                    `https://${appConfig.consoleHostname}/states/home?region=${appConfig.awsRegion}#/executions/details/${e.GenerateCampaignManifestStepFunctionExecutionArn}`
                  );
                return (
                  <Link
                    href={
                      generateCampaignManifestStepFunctionExecutionConsoleUrl.href
                    }
                    target="_blank"
                  >
                    {e.CampaignManifestId}
                  </Link>
                );
              }
              return e.CampaignManifestId;
            },
            sortingField: "CampaignManifestId",
          },
          {
            id: "ManifestType",
            header: "Manifest Type",
            cell: (e) => e.ManifestType,
            sortingField: "ManifestType",
          },
          {
            id: "LineCount",
            header: "Line / Chip Count",
            cell: (e) => e.LineCount,
            sortingField: "LineCount",
          },
          {
            id: "ManifestS3Uri",
            header: "Manifest S3 URI",
            cell: (e) => {
              const [bucket, prefix] = splitS3Uri(e.ManifestS3Uri);
              const manifestS3ConsoleUrll = new URL(
                `https://${appConfig.consoleHostname}/s3/buckets/${bucket}`
              );
              manifestS3ConsoleUrll.searchParams.append("prefix", prefix);

              return (
                <Link href={manifestS3ConsoleUrll.href} target="_blank">
                  {e.ManifestS3Uri}
                </Link>
              );
            },
            sortingField: "ManifestS3Uri",
          },
          {
            id: "DateCreated",
            header: "Date Created",
            cell: (e) => e.DateCreated,
            sortingField: "DateCreated",
          },
          {
            id: "CampaignPrefilteringId",
            header: "Campaign Prefiltering Id",
            cell: (e) => e.CampaignPrefilteringId,
            sortingField: "CampaignPrefilteringId",
          },
          {
            id: "MinConfidenceThreshold",
            header: "Prefiltering Minimum Confidence Threshold",
            cell: (e) => e.MinConfidenceThreshold,
            sortingField: "MinConfidenceThreshold",
          },
        ]}
        items={campaignManifests.slice(
          (currentPageIndex - 1) * pageSize,
          currentPageIndex * pageSize
        )}
        loadingText="Loading jobs"
        visibleColumns={[
          "Status",
          "CampaignManifestId",
          "ManifestType",
          "LineCount",
          "ManifestS3Uri",
          "DateCreated",
          "CampaignPrefilteringId",
          "MinConfidenceThreshold",
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No jobs</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No jobs to display.
            </Box>
          </Box>
        }
        filter={
          <TextFilter filteringText="" filteringPlaceholder="Find jobs" />
        }
        header={
          <TableHeader>
            Ground Truth Input Manifests
            <Button variant="primary" onClick={() => setShowNewManifest(true)}>
              New Ground Truth Input Manifest
            </Button>
          </TableHeader>
        }
        pagination={
          <Pagination
            openEnd={hasNextPage}
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => fetchPage(detail.currentPageIndex)}
            pagesCount={pageCount}
            ariaLabels={{
              nextPageLabel: "Next page",
              previousPageLabel: "Previous page",
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
          />
        }
      />
      <NewCampaignManifestModal
        campaign={campaign}
        onSuccess={() => {
          setShowNewManifest(false);
          refresh();
        }}
        onCancel={() => setShowNewManifest(false)}
        visible={showNewManifest}
      />
    </ContentSection>
  );
};

export default CampaignManifestTable;
